import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, makeStateKey, Optional, PLATFORM_ID, REQUEST, REQUEST_CONTEXT, TransferState } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { environment } from 'environments/environment';
import { SupportedLanguage } from 'app/services/language/languages';

export type LegalPage = 'legal-notice' | 'privacy-policy' | 'terms-service' | 'cookie-policy';
export interface LegalPageContent { content: string; }

@Injectable({ providedIn: 'root' })
export class LegalPageService {
  constructor(
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(REQUEST) private request: Request | null,
    @Optional() @Inject(REQUEST_CONTEXT) private requestContext: { legalPage?: LegalPageContent } | null,
  ) {}

  getLegalNotice(language: SupportedLanguage): Observable<LegalPageContent> {
    return this.load('legal-notice', language);
  }

  getPrivacyPolicy(language: SupportedLanguage): Observable<LegalPageContent> {
    return this.load('privacy-policy', language);
  }

  getTermsService(language: SupportedLanguage): Observable<LegalPageContent> {
    return this.load('terms-service', language);
  }

  getCookiePolicy(language: SupportedLanguage): Observable<LegalPageContent> {
    return this.load('cookie-policy', language);
  }

  load(page: LegalPage, language: SupportedLanguage): Observable<LegalPageContent> {
    const stateKey = makeStateKey<LegalPageContent>(`legal-${page}-${language}`);
    if (this.transferState.hasKey(stateKey)) {
      const content = this.transferState.get(stateKey, null);
      this.transferState.remove(stateKey);
      return of(content);
    }

    if (isPlatformServer(this.platformId)) {
      if (this.requestContext?.legalPage) {
        this.transferState.set(stateKey, this.requestContext.legalPage);
        return of(this.requestContext.legalPage);
      }
      if (!this.request) {
        throw new Error('SSR necesita una URL de petición para cargar la página legal.');
      }
      const url = new URL(`${environment.urlServer}/public/${page}`, this.request.url);
      url.searchParams.set('lang', language.toUpperCase());
      return this.http.get<LegalPageContent>(url.toString()).pipe(
        tap(content => this.transferState.set(stateKey, content)),
      );
    }

    return this.http.get<LegalPageContent>(
      `${environment.urlServer}/public/${page}?lang=${language.toUpperCase()}`,
    );
  }
}
